import { useCallback, useEffect, useState } from 'react';
import { AlertMessage, alertApiRef, useApi } from '@backstage/core-plugin-api';
import {
  IconButton,
  Snackbar,
  Typography,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';
import { TOP_NAV_HEIGHT } from '../TopNav';

const DEFAULT_DISMISS_MS = 3000;
/** Cap queued alerts so long-running sessions cannot grow without bound. */
const MAX_QUEUED_ALERTS = 20;

interface TopAlertDisplayProps {
  transientTimeoutMs?: number;
}

function shouldAutoDismiss(message: AlertMessage): boolean {
  return message.display !== 'permanent';
}

function enqueueAlert(
  messages: AlertMessage[],
  message: AlertMessage,
): AlertMessage[] {
  const next = messages.concat(message);
  if (next.length <= MAX_QUEUED_ALERTS) {
    return next;
  }
  return next.slice(next.length - MAX_QUEUED_ALERTS);
}

export const TopAlertDisplay = ({
  transientTimeoutMs = DEFAULT_DISMISS_MS,
}: TopAlertDisplayProps) => {
  const theme = useTheme();
  const [messages, setMessages] = useState<AlertMessage[]>([]);
  const alertApi = useApi(alertApiRef);

  useEffect(() => {
    const subscription = alertApi.alert$().subscribe(message =>
      setMessages(msgs => enqueueAlert(msgs, message)),
    );
    return () => subscription.unsubscribe();
  }, [alertApi]);

  const [firstMessage] = messages;

  const dismissFirstMessage = useCallback(() => {
    setMessages(msgs => (msgs.length > 0 ? msgs.slice(1) : msgs));
  }, []);

  if (!firstMessage) {
    return null;
  }

  const isAutoDismiss = shouldAutoDismiss(firstMessage);

  return (
    <Snackbar
      open
      // Single auto-dismiss path: rely on Snackbar only (no parallel setTimeout).
      autoHideDuration={isAutoDismiss ? transientTimeoutMs : undefined}
      onClose={(_, reason) => {
        // Permanent alerts must only be dismissed via the close button.
        if (
          isAutoDismiss &&
          (reason === 'timeout' ||
            reason === 'clickaway' ||
            reason === 'escapeKeyDown')
        ) {
          dismissFirstMessage();
        }
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      style={{
        top: TOP_NAV_HEIGHT + 8,
        zIndex: theme.zIndex.snackbar,
      }}
    >
      <Alert
        variant="filled"
        severity={firstMessage.severity}
        action={
          <IconButton color="inherit" size="small" onClick={dismissFirstMessage}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        <Typography component="span" variant="body2">
          {String(firstMessage.message)}
          {messages.length > 1 && (
            <em>{` (+${messages.length - 1} more)`}</em>
          )}
        </Typography>
      </Alert>
    </Snackbar>
  );
};
